import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import moment from 'moment'

import { IProps } from './IProps'
import { NoTableDataText } from './style'

export const ProjectedEarningsChart: FunctionComponent<IProps> = ({
  projected,
  revenue,
}) => {
  const { t } = useTranslation()
  const [earningsData, setEarningsData] = useState([])
  const [thisYearValue, setThisYearValue] = useState([])

  useEffect(() => {
    if (projected) {
      const objectValues = Object.values(projected)
      const result = objectValues.reduce((c, v) => c.concat(v), [])
      setEarningsData(result)
    }
  }, [projected, revenue])

  useEffect(() => {
    if (earningsData.length > 0) {
      const summed = earningsData.reduce((acc, curr) => {
        const date = moment(curr.date)
        const key = `${date.format('YYYY')}-${date.format('MM')}`
        const previousPrice = acc[key]
        let currentPrice = Number(curr.amount)
        if (previousPrice) {
          currentPrice += Number(previousPrice)
        }
        return { ...acc, [key]: currentPrice }
      }, {})

      const sortedArray = Object.keys(summed)
        .sort()
        .map(val => {
          return summed[val]
        })

      setThisYearValue(sortedArray)
    }
  }, [earningsData])

  const [labels, values] = useMemo(() => {
    if (typeof revenue === 'undefined') {
      return [[], []]
    }

    const months = []
    const currentDate = moment().startOf('month')
    const endDate = moment().add(1, 'month').endOf('month')
    const valuesByKey: { [dateKey: string]: number } = {}

    do {
      months.push(currentDate.format('MMMM'))
      valuesByKey[currentDate.format('YYYY-MM')] = 0.0
      currentDate.add(1, 'month')
    } while (endDate.diff(currentDate, 'months') >= 0)

    for (const [dateKey, amount] of Object.entries(revenue)) {
      valuesByKey[dateKey] = amount / 100
    }
    const filteredDates = Object.values(revenue).slice(0, 3)
    return [months, filteredDates]
  }, [revenue])

  const colorArray: any = []

  if (thisYearValue && values) {
    for (let i = 0; i < thisYearValue.length; i++) {
      if (thisYearValue[i] < values[i]) {
        colorArray.push('rgb(75, 192, 192)')
      } else {
        colorArray.push('rgb(255, 99, 132)')
      }
    }
  }

  const data = {
    labels,
    datasets: [
      {
        label: 'This year',
        data: thisYearValue,
        backgroundColor: 'rgb(53, 162, 235)',
        stack: 'Stack 0',
      },

      {
        label: 'Last year',
        data: values,
        backgroundColor: colorArray,
        stack: 'Stack 1',
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
      },
    },
  }

  return (
    <>
      {Array.isArray(thisYearValue) && thisYearValue.length > 0 ? (
        <Bar options={options} data={data} />
      ) : (
        <NoTableDataText>
          {t(`📉 There are no projected earnings available`)}
        </NoTableDataText>
      )}
    </>
  )
}
